import { T } from 'src/shared/helpers';
import React, { FC } from 'react';
import styled from 'styled-components';
import { BasicButton, GhostButton } from '../../../../../widgets/buttons';
import ExpandMoreIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import CommandMenu from './commandMenu';

export type SceneEditorActionKind = 'add' | 'action' | 'danger';

export interface SceneEditorAction {
    /**
     * COME SI CHIAMA IL COMANDO, in due parole: «Duplicate», «Remove». È il nome che si legge —
     * nel «…» di una riga e nel pannello del mucchio — e quello che la barra dà da leggere a chi
     * non vede il glifo. Un nome per macchine («CLONE») lì diventa una parola gridata.
     */
    name: string;
    /** La riga che spiega cosa fa: «A copy in this scene». Sotto il nome nel menu, in punta al puntatore nella barra. */
    tooltip: string;
    icon: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    kind?: SceneEditorActionKind;
    /**
     * Per i comandi che accendono e spengono qualcosa invece di eseguirla una volta:
     * il bottone resta premuto finché la condizione vale, e lo dichiara con `aria-pressed`.
     */
    active?: boolean;
    /**
     * QUANDO IL COMANDO HA PIÙ STRADE. Un modello arriva da un caricamento o dalla libreria, una
     * luce nasce di quattro tipi: quale lo dice chi clicca, e il bottone apre un menu invece di
     * eseguire. `onClick` resta come ripiego se il menu è vuoto, e non viene mai chiamato finché
     * c'è anche una sola voce.
     */
    menu?: SceneEditorAction[];
}

interface SceneEditorActionBarProps {
    actions: SceneEditorAction[];
    /** Chiude la barra a destra del distruttivo: le viste della libreria, non un comando. */
    trailing?: React.ReactNode;
}

// LA BARRA VA A CAPO, NON NASCONDE. Scorreva in orizzontale con la barra di scorrimento
// invisibile: bastava un comando in più — «Seleziona tutto» — perché l'ultimo, il cestino,
// finisse fuori dal bordo senza alcun segno che ci fosse ancora. Un comando nascosto senza
// avviso è un comando perduto. Andando a capo la riga si allunga quando serve, e nella
// maggior parte delle sezioni non serve.
const ActionBar = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 3px;
    min-height: 44px;
    padding: 6px 8px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
`;

const ActionButton = styled(GhostButton)<{ $active?: boolean }>`
    flex: 0 0 auto;
    min-width: 30px;
    height: 32px;
    min-height: 32px;
    padding: 0 6px;
    border-radius: 6px;
    font-size: 12px;
    background: ${(props) => (props.$active ? props.theme.app.pageBackgroundColor : 'transparent')};
    box-shadow: ${(props) => (props.$active ? `inset 0 0 0 1px ${props.theme.app.borderColor}` : 'none')};

    > svg {
        width: 17px;
        height: 17px;
        margin: 0;
    }
`;

// «Aggiungi» non è l'azione principale della pagina — quella è Save, in fascia. Qui è la
// prima voce di una barra fra tante barre uguali, e dipingerla d'arancione metteva quattro
// primari sullo schermo che si contendevano lo stesso significato.
const AddActionButton = styled(BasicButton)`
    flex: 0 0 auto;
    height: 32px;
    min-height: 32px;
    padding: 0 9px;
    border-radius: 6px;
    font-size: 12px;
    gap: 4px;

    > svg {
        width: 17px;
        height: 17px;
        margin: 0;
    }
`;

// Il distruttivo si riconosce dal COLORE del glifo, non da un rettangolo rosso: pieno,
// pesa più del comando che lo precede e attira il clic che non deve attirare.
const DestructiveActionButton = styled(GhostButton)`
    flex: 0 0 auto;
    min-width: 30px;
    height: 32px;
    min-height: 32px;
    padding: 0 6px;
    border-radius: 6px;
    font-size: 12px;
    color: ${(props) => props.theme.app.component.button.destructive.default.backgroundColor};

    &:hover:not([disabled]),
    &:active:not([disabled]) {
        color: ${(props) => props.theme.app.component.button.destructive.hover.backgroundColor};
    }

    > svg {
        width: 17px;
        height: 17px;
        margin: 0;
    }
`;

// Il vuoto fra «Aggiungi» e il resto: i comandi che agiscono su una selezione stanno
// dall'altra parte della barra, e il distruttivo chiude la fila.
const ActionSpacer = styled.span`
    flex: 1 1 auto;
`;

const ActionDivider = styled.span`
    width: 1px;
    height: 24px;
    margin: 0 3px;
    background: ${(props) => props.theme.app.borderColor};
`;

// La freccetta che dice «questo bottone apre un menu, non esegue». Senza, chi guarda
// non sa che c'è una scelta da fare finché non clicca.
const DropdownChevron = styled.span`
    display: inline-flex;
    align-items: center;
    margin-left: -2px;
    margin-right: -4px;

    > svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

const SceneEditorActionBar: FC<SceneEditorActionBarProps> = ({ actions, trailing }) => {
    let renderedDangerDivider = false;
    let renderedSpacer = false;

    return (
        <ActionBar role='toolbar' aria-label={T._('Scene editor actions', 'Admin')}>
            {actions.map((action) => {
                const kind = action.kind ?? 'action';
                const showLabel = kind === 'add';
                const shouldRenderDivider = kind === 'danger' && !renderedDangerDivider;
                const shouldRenderSpacer = kind !== 'add' && !renderedSpacer;

                if (shouldRenderDivider) renderedDangerDivider = true;
                if (shouldRenderSpacer) renderedSpacer = true;

                const buttonProps = {
                    type: 'button' as const,
                    // CON UN MENU SOTTO, IL BOTTONE APRE E BASTA. Lasciandogli anche il suo `onClick` si
                    // otteneva un clic che faceva le due cose insieme — «Add object» spalancava la
                    // scelta dei file *e* apriva il menu — perché il clic parte dal bottone e sale al
                    // guscio che comanda il menu.
                    onClick: action.menu?.length ? undefined : action.onClick,
                    disabled: action.disabled,
                    title: action.tooltip,
                    'aria-label': action.name,
                    'aria-pressed': action.active === undefined ? undefined : action.active
                };

                const hasMenu = !!action.menu?.length;

                const content = showLabel ? (
                    <>
                        {action.icon}
                        <span>{action.name}</span>
                        {hasMenu && (
                            <DropdownChevron aria-hidden='true'>
                                <ExpandMoreIcon />
                            </DropdownChevron>
                        )}
                    </>
                ) : (
                    <>
                        {action.icon}
                        {hasMenu && (
                            <DropdownChevron aria-hidden='true'>
                                <ExpandMoreIcon />
                            </DropdownChevron>
                        )}
                    </>
                );

                const button =
                    kind === 'add' ? (
                        <AddActionButton {...buttonProps}>{content}</AddActionButton>
                    ) : kind === 'danger' ? (
                        <DestructiveActionButton {...buttonProps}>{content}</DestructiveActionButton>
                    ) : (
                        <ActionButton {...buttonProps} $active={action.active}>
                            {content}
                        </ActionButton>
                    );

                return (
                    <React.Fragment key={action.name}>
                        {shouldRenderSpacer && <ActionSpacer aria-hidden='true' />}
                        {shouldRenderDivider && <ActionDivider aria-hidden='true' />}
                        {action.menu?.length ? <CommandMenu actions={action.menu} trigger={button} /> : button}
                    </React.Fragment>
                );
            })}
            {trailing && (
                <>
                    <ActionDivider aria-hidden='true' />
                    {trailing}
                </>
            )}
        </ActionBar>
    );
};

export default SceneEditorActionBar;
